// Light/dark theming.
//
// Privacy note: the only thing this module persists is the string "light" or
// "dark" under one localStorage key. No genome data touches storage — the
// in-memory-only guarantee for your actual data is unchanged.

import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'i-seng:theme';

/** localStorage can throw (private mode, disabled storage) — never let it break the app. */
function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

function writeStored(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* preference just won't survive a reload — not worth surfacing */
  }
}

function systemTheme(): Theme {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

/**
 * Stamp `data-theme` on <html> before React renders, so the first paint is
 * already the right theme instead of flashing dark. Call once from main.tsx.
 */
export function applyInitialTheme(): void {
  document.documentElement.setAttribute('data-theme', readStored() ?? systemTheme());
}

/**
 * Resolved theme + a toggle.
 *
 * Until the user picks explicitly we follow the OS and keep following it live;
 * the first toggle pins the choice. The `data-theme` attribute is also written
 * synchronously on toggle so chart colors (which read from a static map, not
 * the DOM) and CSS flip in the same frame.
 */
export function useTheme(): { theme: Theme; toggle: () => void } {
  const [pref, setPref] = useState<Theme | 'system'>(() => readStored() ?? 'system');
  const [system, setSystem] = useState<Theme>(systemTheme);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: light)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? 'light' : 'dark');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const theme: Theme = pref === 'system' ? system : pref;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setPref((current) => {
      const resolved = current === 'system' ? systemTheme() : current;
      const next: Theme = resolved === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      writeStored(next);
      return next;
    });
  }, []);

  return { theme, toggle };
}

/**
 * Chart colors for Recharts, which needs literal color strings rather than
 * `var(--token)`. Kept as a static map (not read back off the DOM) so a theme
 * flip renders in one pass with no computed-style race.
 *
 * These MUST mirror the corresponding tokens in styles.css.
 */
export interface ChartColors {
  grid: string;
  axis: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  cursor: string;
  bar: string;
  palette: string[];
}

export const CHART_COLORS: Record<Theme, ChartColors> = {
  dark: {
    grid: '#263049',
    axis: '#97a3bd',
    tooltipBg: '#121826',
    tooltipBorder: '#263049',
    tooltipText: '#e7ecf5',
    cursor: 'rgba(124,156,255,0.08)',
    bar: '#5eead4',
    palette: [
      '#5eead4', '#7c9cff', '#f0abfc', '#4ade80', '#fbbf24', '#f87171',
      '#38bdf8', '#a78bfa', '#fb923c', '#34d399', '#e879f9', '#60a5fa',
    ],
  },
  light: {
    grid: '#dbe2f0',
    axis: '#55617c',
    tooltipBg: '#ffffff',
    tooltipBorder: '#d5dced',
    tooltipText: '#131a29',
    cursor: 'rgba(79,70,229,0.07)',
    bar: '#0f766e',
    palette: [
      '#0f766e', '#4f46e5', '#a21caf', '#15803d', '#b45309', '#b91c1c',
      '#0369a1', '#6d28d9', '#c2410c', '#047857', '#a21caf', '#1d4ed8',
    ],
  },
};
